import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

import { SideNavState } from './menu-types';
import { MenuItemViewModel, MenuSectionConfig } from './menu-grammar';
import { MenuService } from 'src/app/services/menu.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SidenavService } from 'src/app/services/sidenav.service';
import { DataRefreshService } from 'src/app/services/data-refresh.service';
import { YearGroupService } from '../../services/year-group.service';
import { User } from 'src/app/types/user-types';
import { ButtonComponent } from '../ui/button/button.component';
import { MenuSectionComponent } from './menu-section/menu-section.component';
import { SubscribedMenuComponent } from './menus/subscribed-menu/subscribed-menu.component';
import { FreeTasterMenuComponent } from './menus/free-taster-menu/free-taster-menu.component';
import { LiveLessonNotificationComponent } from '../live-lesson/live-lesson-notification/live-lesson-notification.component';
import { LiveLessonCountdownComponent } from '../live-lesson/live-lesson-countdown/live-lesson-countdown.component';
import { LiveLessonScheduleComponent } from '../live-lesson/live-lesson-schedule/live-lesson-schedule.component';
import { LiveLessonService } from "../../services/live-lesson.service";
import { CurrentTimeService } from "../../services/current-time.service";
import { FeatureStore } from "../../stores/feature-store";
import { SubscriptionStore } from "../../stores/subscription-store";
import { OnboardingWelcomeSettingsService } from "../../services/onboarding-welcome-settings.service";
import { CampaignStore } from "../../stores/campaign-store";
import { catchError, of, take } from "rxjs";
import { LiveLesson } from "../../types/live-lesson-types";

@Component({
  selector: 'app-side-nav-menu',
  templateUrl: './side-nav-menu.component.html',
  styleUrl: './side-nav-menu.component.scss',
  imports: [
    NgClass,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    ButtonComponent,
    MenuSectionComponent,
    SubscribedMenuComponent,
    FreeTasterMenuComponent,
    LiveLessonNotificationComponent,
    LiveLessonCountdownComponent,
    LiveLessonScheduleComponent
  ],
})
export class SideNavMenuComponent {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthenticationService);
  private sidenavSvc = inject(SidenavService);
  private dataRefreshService = inject(DataRefreshService);
  private yearGroupSvc = inject(YearGroupService);
  liveLessonSvc = inject(LiveLessonService);
  private currentTimeSvc = inject(CurrentTimeService);
  private onboardingWelcomeSettingsSvc = inject(OnboardingWelcomeSettingsService);
  private campaignStore = inject(CampaignStore);
  featureStore = inject(FeatureStore);
  subscriptionStore = inject(SubscriptionStore);

  menuService = inject(MenuService);

  sidenavState = input<SideNavState>(SideNavState.FULL);
  iconOnly = computed(() => this.sidenavState() === SideNavState.SLIM);

  closeSidenav = output();
  logout = output();
  toggleDesktopSidenav = output();

  userData: User;
  userInitial = signal<string>('');
  yearGroup = signal<string>('');
  avatar = signal<string>('');
  fullUserName = signal<string>('');

  settingsSelectedId = signal<string | null>(null);
  onboardingWelcomeActive = signal(false);

  liveLesson = computed(() => {
    if (!this.featureStore.hasLiveLessons()) return null;
    const now = this.currentTimeSvc.currentTime();
    const bufferMs = 60 * 60 * 1000;
    return this.liveLessonSvc.allLessons().find(
      (l) => new Date(l.starts_at) <= now &&
        new Date(new Date(l.ends_at!).getTime() + bufferMs) >= now &&
        this.canAccessLesson(l),
    ) ?? null;
  });

  scheduleOpen = signal(false);

  // Feeds the countdown card — tier-gated the same way `liveLesson` above already is, so the
  // card never teases a lesson the viewer can't actually get into.
  upcomingLessons = computed(() => {
    if (!this.featureStore.hasLiveLessons()) return [];
    const now = this.currentTimeSvc.currentTime();
    const cutoff = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
    return this.liveLessonSvc.allLessons()
      .filter((l) => new Date(l.starts_at) > now && new Date(l.starts_at) <= cutoff && this.canAccessLesson(l))
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  });

  // Feeds the "View both/all live lessons" schedule drawer — deliberately not tier-filtered or
  // capped at 5 days, matching how LiveLessonComponent's own schedule list browses everything
  // upcoming regardless of the viewer's tier.
  scheduleLessons = computed(() => {
    const now = this.currentTimeSvc.currentTime();
    return this.liveLessonSvc.allLessons()
      .filter((l) => new Date(l.ends_at!).getTime() >= now.getTime())
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  });

  ngOnInit() {
    this.setUserInfo();
    // Subscribed-menu/free-taster-menu each fetch campaigns themselves when mounted, but
    // neither mounts during onboarding (see showCampaignsInOnboarding()) — fetch here too so
    // campaigns are loaded regardless of which context ends up active. No-ops if already
    // loading/loaded.
    this.campaignStore.fetch();
    if (this.featureStore.hasLiveLessons() && this.liveLessonSvc.allLessons().length === 0) {
      this.liveLessonSvc.getAll().pipe(take(1)).subscribe();
    }
    this.onboardingWelcomeSettingsSvc.getSettings().pipe(
      take(1),
      catchError(() => of({ active: false, video_url_premium: null, video_url_standard: null })),
    ).subscribe((settings) => this.onboardingWelcomeActive.set(settings.active));
    this.updateSettingsSelection(this.router.url);

    this.dataRefreshService.dataRefreshed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setUserInfo());

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.updateSettingsSelection(event.urlAfterRedirects));
  }

  private updateSettingsSelection(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    this.settingsSelectedId.set(path === '/account' ? 'settings' : null);
  }

  setUserInfo() {
    this.userData = this.authService.getUserData() ?? ({} as User);

    const firstName = this.userData.first_name as string;
    const lastName = this.userData.last_name as string;

    this.avatar.set(this.userData.avatar as string);
    this.yearGroup.set(
      this.yearGroupSvc.formatYearGroupDisplay(this.userData.year_group),
    );

    let fullName = 'User';
    if (firstName && lastName) {
      const formatName = (name: string) =>
        name?.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      fullName = `${formatName(firstName)} ${formatName(lastName)}`;
      this.userInitial.set(firstName.charAt(0).toUpperCase());
    } else if (this.userData.name) {
      fullName = this.userData.name as string;
      this.userInitial.set(this.userData?.name.charAt(0).toUpperCase());
    }
    this.fullUserName.set(fullName);
  }

  onFeatureItemSelect(item: MenuItemViewModel) {
    if (item.url) {
      this.router.navigate([item.url]);
    }
    this.closeSidenavOnMobile();
  }

  /** On mobile the sidenav is an overlay drawer covering the page — leaving it open after a
   * nav item routes elsewhere hides the page the user just navigated to. Desktop's full/slim
   * layouts are persistent, so they're left alone. */
  private closeSidenavOnMobile(): void {
    if (this.sidenavState() === SideNavState.MOBILE) {
      this.closeSidenav.emit();
    }
  }

  goToSettings(): void {
    this.router.navigate(['/account'], { queryParams: { tab: 'account' } });
    this.closeSidenavOnMobile();
  }

  openSupport(): void {
    if (window.Intercom) {
      window.Intercom('show');
    } else {
      console.warn('Intercom not initialized');
    }
  }

  navigateToDashboard() {
    this.sidenavSvc.naviagteToDashboard();
  }

  goToHowToUse(): void {
      this.router.navigateByUrl('/faq');
      this.closeSidenavOnMobile();
  }

  canAccessLesson(lesson: LiveLesson): boolean {
    const tier = lesson.access_tier;
    const hasPremium = this.featureStore.hasFeature('live-lessons-feature');
    const isSubscribed = this.subscriptionStore.isSubscribed();
    if (tier === 'free') return true;
    if (tier === 'standard') return isSubscribed || hasPremium;
    if (tier === 'premium') return hasPremium;
    return true;
  }
}
